import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { FEED_QK, UNREAD_QK } from '@/hooks/use-comunicado-mutations';

const VIEWPORT_DELAY_MS = 800;

function useReadMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      pb.send(`/api/comunicados/${id}/read`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FEED_QK });
      qc.invalidateQueries({ queryKey: UNREAD_QK });
    },
  });
}

function observeOnce(node: globalThis.Element, fire: () => void): () => void {
  let timer: number | null = null;
  const obs = new globalThis.IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          timer = window.setTimeout(fire, VIEWPORT_DELAY_MS);
        } else if (timer !== null) {
          window.clearTimeout(timer);
          timer = null;
        }
      }
    },
    { threshold: 0 }
  );
  obs.observe(node);
  return () => {
    obs.disconnect();
    if (timer !== null) window.clearTimeout(timer);
  };
}

export function useMarkRead(id: string, isRead: boolean) {
  const ref = useRef<HTMLElement | null>(null);
  const mark = useReadMutation(id);
  useEffect(() => {
    if (isRead || !ref.current) return;
    return observeOnce(ref.current, () => mark.mutate());
  }, [id, isRead, mark]);
  return ref;
}
